import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { F as FormController } from './form.js';
import { O as Owner } from './Business.js';
import { c as BusinessFormClickActions } from './business-form-types.js';
import { i as identitySchema } from './business-identity-schema.js';
import { B as Button } from './button.js';
import { d as defineCustomElement$9 } from './custom-popper2.js';
import { d as defineCustomElement$8 } from './form-control-date2.js';
import { d as defineCustomElement$7 } from './form-control-error-text2.js';
import { d as defineCustomElement$6 } from './form-control-number-masked2.js';
import { d as defineCustomElement$5 } from './form-control-select2.js';
import { d as defineCustomElement$4 } from './form-control-text2.js';
import { d as defineCustomElement$3 } from './form-control-tooltip2.js';
import { d as defineCustomElement$2 } from './identity-address-form.js';
import { d as defineCustomElement$1 } from './owner-form-inputs2.js';

const BusinessOwnerFormCore = /*@__PURE__*/ proxyCustomElement(class BusinessOwnerFormCore extends HTMLElement {
    loadingWatcher() {
        this.formLoading.emit(this.isLoading);
    }
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.ownerSubmitted = createEvent(this, "ownerSubmitted", 7);
        this.clickEvent = createEvent(this, "click-event", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.getData = () => {
            this.isLoading = true;
            this.getOwner({
                onSuccess: (response) => {
                    this.instantiateOwner(response.data);
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => this.isLoading = false
            });
        };
        this.sendData = () => {
            this.isLoading = true;
            return new Promise((resolve) => {
                if (this.ownerId) {
                    this.patchOwner({
                        payload: this.payload,
                        onSuccess: (response) => {
                            this.submitted.emit({
                                data: response,
                                metadata: { ownerID: response.data.id }
                            });
                            this.ownerSubmitted.emit({ id: response.data.id });
                            this.instantiateOwner(response.data);
                            resolve(true);
                        },
                        onError: ({ error, code, severity }) => {
                            this.submitted.emit({ data: { error } });
                            this.errorEvent.emit({
                                message: error,
                                errorCode: code,
                                severity: severity
                            });
                            resolve(false);
                        },
                        final: () => this.isLoading = false
                    });
                }
                else {
                    this.postOwner({
                        payload: this.payload,
                        onSuccess: (response) => {
                            this.submitted.emit({
                                data: response,
                                metadata: { ownerID: response.data.id }
                            });
                            this.ownerSubmitted.emit({ id: response.data.id });
                            this.instantiateOwner(response.data);
                            resolve(true);
                        },
                        onError: ({ error, code, severity }) => {
                            this.submitted.emit({ data: { error } });
                            this.errorEvent.emit({
                                message: error,
                                errorCode: code,
                                severity: severity
                            });
                            resolve(false);
                        },
                        final: () => this.isLoading = false
                    });
                }
            });
        };
        this.instantiateOwner = async (data) => {
            this.owner = Object.assign({}, new Owner(data));
            await this.formController.setInitialValues(this.owner);
        };
        this.handleAddOwner = () => {
            const eventName = this.ownerId ? BusinessFormClickActions.updateOwner : BusinessFormClickActions.addOwner;
            this.clickEvent.emit({ name: eventName });
        };
        this.handleRemoveOwner = () => {
            this.removeOwner(this.ownerId);
            this.clickEvent.emit({ name: BusinessFormClickActions.removeOwner });
        };
        this.isLoading = false;
        this.formController = undefined;
        this.errors = {};
        this.owner = {};
        this.ownerId = undefined;
        this.businessId = undefined;
        this.getOwner = undefined;
        this.patchOwner = undefined;
        this.postOwner = undefined;
        this.allowOptionalFields = undefined;
        this.removeOwner = undefined;
        this.newFormOpen = undefined;
        this.ownersLength = undefined;
        this.validate = this.validate.bind(this);
        this.submit = this.submit.bind(this);
        this.validateAndSubmit = this.validateAndSubmit.bind(this);
    }
    async returnOwnerData() {
        return this.ownerId;
    }
    async validate() {
        return this.formController.validate();
    }
    async submit() {
        const isValid = await this.validate();
        if (isValid) {
            return this.sendData();
        }
    }
    async validateAndSubmit(event) {
        event.preventDefault();
        const isValid = await this.validate();
        if (isValid) {
            this.submit();
        }
    }
    get payload() {
        let formValues = new Owner(this.formController.values.getValue()).payload;
        if (this.ownerId) {
            return formValues;
        }
        else {
            return Object.assign(Object.assign({}, formValues), { business_id: this.businessId });
        }
    }
    get formTitle() {
        return this.ownerId ? 'Edit Business Owner' : 'Add Business Owner';
    }
    get submitButtonText() {
        return this.ownerId ? 'Update' : 'Add';
    }
    get showRemoveButton() {
        const blankForm = !this.ownerId && this.newFormOpen;
        if (this.ownersLength <= 1) {
            return false;
        }
        else {
            if (blankForm) {
                return true;
            }
            else if (this.ownerId && !this.newFormOpen) {
                return true;
            }
        }
    }
    componentWillLoad() {
        this.formController = new FormController(identitySchema('owner', this.allowOptionalFields));
        this.ownerId ? this.getData() : this.instantiateOwner({});
    }
    componentDidLoad() {
        this.formController.errors.subscribe(errors => (this.errors = Object.assign({}, errors)));
    }
    render() {
        return (h("form", { key: '27f0d989f017bd03b84cf8c994fa96f9a65aad08', onSubmit: this.validateAndSubmit }, h("fieldset", { key: '2c793f1f8777181e5db0a4cae5b9994d9188685e' }, h("legend", { key: 'a8951fcd990c8136cf6672424869f5c96def3cef', class: "fw-semibold fs-5" }, this.formTitle), h("br", { key: 'faaf0cc27c448452a5172ad7fcfcf510ec586d2b' }), h("div", { key: '486638ad24414a05f84ada3f37a89d0cc12e7ffb', class: "row gy-3" }, h("owner-form-inputs", { key: 'bdfe7cb3305f31d93321319ef14eee3e5b331381', ownerDefaultValue: this.formController.getInitialValues(), errors: this.errors, formController: this.formController }), h("div", { key: '9e5c63e0637e0bffe5ab77a1f73c203704115479', class: "d-flex gap-2 justify-content-start" }, h(Button, { key: '85de4b69c697ebf96778f538b1535bd1fb2c43e7', variant: "secondary", type: "button", onClick: this.handleRemoveOwner, hidden: !this.showRemoveButton }, "Remove"), h(Button, { key: '1dd026432f19f84ae2142101191e511ebd62c2b3', variant: "primary", type: "submit", onClick: this.handleAddOwner, disabled: this.isLoading }, this.submitButtonText))), h("hr", { key: '2570611bbe663e2e128f03806ecb036ce750c80b' }))));
    }
    static get watchers() { return {
        "isLoading": ["loadingWatcher"]
    }; }
}, [0, "owner-form-core", {
        "ownerId": [1, "owner-id"],
        "businessId": [1, "business-id"],
        "getOwner": [16],
        "patchOwner": [16],
        "postOwner": [16],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "removeOwner": [16],
        "newFormOpen": [4, "new-form-open"],
        "ownersLength": [2, "owners-length"],
        "isLoading": [32],
        "formController": [32],
        "errors": [32],
        "owner": [32],
        "returnOwnerData": [64],
        "validate": [64],
        "submit": [64],
        "validateAndSubmit": [64]
    }, undefined, {
        "isLoading": ["loadingWatcher"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["owner-form-core", "custom-popper", "form-control-date", "form-control-error-text", "form-control-number-masked", "form-control-select", "form-control-text", "form-control-tooltip", "justifi-identity-address-form", "owner-form-inputs"];
    components.forEach(tagName => { switch (tagName) {
        case "owner-form-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessOwnerFormCore);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "form-control-date":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "form-control-number-masked":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "justifi-identity-address-form":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "owner-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessOwnerFormCore as B, defineCustomElement as d };
